import type { SetStateAction, Dispatch, SVGProps } from 'react';
import React, { useCallback, useMemo, useContext } from 'react';
import cx from 'classnames';
import { TreeContext } from '../../../contexts/TreeContext';
import ActionButton from './ActionButton';
import clipboard from '@ingka/ssr-icon/paths/clipboard';
import clipboardStrikethrough from '@ingka/ssr-icon/paths/clipboard-strikethrough';
import copy from '@ingka/ssr-icon/paths/copy';
import scissors from '@ingka/ssr-icon/paths/scissors';
import trashCan from '@ingka/ssr-icon/paths/trash-can';
import takeBack from '@ingka/ssr-icon/paths/take-back';
import arrowUp from '@ingka/ssr-icon/paths/arrow-up';
import arrowDown from '@ingka/ssr-icon/paths/arrow-down';

import { useHoverActionsNodeConfig } from './useHoverActionsNodeConfig';
import styles from './HoverActions.module.scss';
import type { NodeBase, NodeItem } from '../../../entities/Node';
import { NODE_TYPE_ITEM } from '../../../utils/types/nodeTypes';

interface HoverActionsProps {
  node: NodeBase | NodeItem;
}

export const HoverActions: React.FC<HoverActionsProps> = ({ node }) => {
  const { actions, onHoverOnly, showPaste } = useHoverActionsNodeConfig(node);
  const { setLoading } = useContext(TreeContext);

  const setIsLoading = useCallback(() => {
    setLoading(true);
  }, [setLoading]);

  const callback = useCallback(() => {
    setLoading(false);
  }, [setLoading]);

  const rightButtons = useMemo(
    () => getRightButtons(actions, node.id, setLoading, callback),
    [node.id, actions, callback, setLoading],
  );

  return (
    <>
      <div
        className={cx(styles.container, styles.left, {
          [styles.show]: !onHoverOnly,
        })}
      >
        {!!showPaste && (
          <ActionButton
            id={`${node.id}-paste`}
            iconPath={'pasteAction' in actions ? clipboard : clipboardStrikethrough}
            onClick={() => {
              if ('pasteAction' in actions && actions.pasteAction) {
                actions.pasteAction(callback);
                setIsLoading();
              }
            }}
            label="Paste"
          />
        )}
      </div>
      <div
        className={cx(styles.container, styles.right, {
          [styles.show]: !onHoverOnly,
          [styles.hoverItem]: node.type === NODE_TYPE_ITEM,
        })}
      >
        {rightButtons.map(({ iconPath, action, label, id }) => (
          <ActionButton
            id={id}
            key={id}
            iconPath={iconPath}
            label={label}
            onClick={action}
          />
        ))}
      </div>
    </>
  );
};

interface Action {
  id: string;
  iconPath: (prefix?: string | undefined) => SVGProps<SVGElement>[];
  action?: () => void;
  label: string;
}

function getRightButtons(
  actions: {
    nodeMenuCopy?: () => void;
    markForCategorization?: () => void;
    nodeMenuCut?: () => void;
    openAlterSuggestionsDialog?: () => void;
    openDialogRemoveItem?: () => void;
    nodeMenuMoveUp?: (callback: () => void) => void;
    nodeMenuMoveDown?: (callback: () => void) => void;
    pasteAction?: () => void;
  },
  nodeId: string,
  setLoading: Dispatch<SetStateAction<boolean>>,
  callback: () => void,
): Action[] {
  const buttons: Action[] = [];
  const id = (suffix: string) => `${nodeId}-${suffix}`;

  if (actions.nodeMenuCopy) {
    buttons.push({
      id: id('copy'),
      iconPath: copy,
      action: actions.nodeMenuCopy,
      label: 'Copy',
    });
  }

  if (actions.markForCategorization || actions.nodeMenuCut) {
    buttons.push({
      id: id('cut'),
      iconPath: scissors,
      action: actions.markForCategorization || actions.nodeMenuCut,
      label: 'Cut',
    });
  }

  if (actions.openAlterSuggestionsDialog) {
    buttons.push({
      id: id('take-back'),
      iconPath: takeBack,
      action: actions.openAlterSuggestionsDialog,
      label: 'Alter',
    });
  }

  if (actions.openDialogRemoveItem) {
    buttons.push({
      id: id('remove'),
      iconPath: trashCan,
      action: actions.openDialogRemoveItem,
      label: 'Remove',
    });
  }

  if (actions.nodeMenuMoveUp) {
    buttons.push({
      id: id('move-up'),
      iconPath: arrowUp,
      action: () => {
        setLoading(true);
        if (actions.nodeMenuMoveUp) actions?.nodeMenuMoveUp(callback);
      },
      label: 'Move',
    });
  }

  if (actions.nodeMenuMoveDown) {
    buttons.push({
      id: id('move-down'),
      iconPath: arrowDown,
      action: () => {
        setLoading(true);
        if (actions?.nodeMenuMoveDown) actions?.nodeMenuMoveDown(callback);
      },
      label: 'Move',
    });
  }

  return buttons;
}
